/**
 * DV-TEMP-004: Temporal Web Links Component
 *
 * Displays links to Temporal Web UI for debugging workflow executions.
 * Shows workflow ID, run ID, and provides deep links to Temporal Web.
 */

import React from 'react';
import { ExternalLink, Activity, Clock, AlertTriangle, Info } from 'lucide-react';
import { useTemporalLinks } from '@/lib/temporal-links';

interface TemporalLinksProps {
  executionId: string;
  workflowRunId?: string;
  className?: string;
  compact?: boolean;
  'data-testid'?: string;
}

interface TemporalLinkButtonProps {
  href: string;
  label: string;
  icon: React.ReactNode;
  description: string;
  variant?: 'primary' | 'secondary';
}

const TemporalLinkButton: React.FC<TemporalLinkButtonProps> = ({
  href,
  label,
  icon,
  description,
  variant = 'secondary'
}) => {
  const baseClasses = "inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-md transition-colors";
  const variantClasses = variant === 'primary'
    ? "bg-blue-600 text-white hover:bg-blue-700"
    : "bg-gray-700 text-gray-200 hover:bg-gray-600 border border-gray-600";

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`${baseClasses} ${variantClasses}`}
      title={description}
    >
      {icon}
      <span>{label}</span>
      <ExternalLink className="w-3 h-3" />
    </a>
  );
};

export function TemporalLinks({
  executionId,
  workflowRunId,
  className = '',
  compact = false,
  'data-testid': testId = 'temporal-links'
}: TemporalLinksProps) {
  const { identifiers, links, formatted, canGenerate } = useTemporalLinks(
    executionId,
    workflowRunId
  );

  if (!canGenerate) {
    return (
      <div
        className={`text-xs text-gray-500 ${className}`}
        data-testid={testId}
      >
        <div className="flex items-center gap-1">
          <Info className="w-3 h-3" />
          <span>No Temporal workflow links available</span>
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <div
        className={`flex items-center gap-2 ${className}`}
        data-testid={testId}
      >
        <div className="flex items-center gap-1 text-xs text-gray-400">
          <Activity className="w-3 h-3" />
          <span>Temporal:</span>
        </div>

        {links.workflow && (
          <TemporalLinkButton
            href={links.workflow}
            label="Workflow"
            icon={<Activity className="w-3 h-3" />}
            description="View workflow execution in Temporal Web"
            variant="primary"
          />
        )}

        {links.history && (
          <TemporalLinkButton
            href={links.history}
            label="History"
            icon={<Clock className="w-3 h-3" />}
            description="View workflow execution history"
          />
        )}
      </div>
    );
  }

  return (
    <div
      className={`space-y-3 ${className}`}
      data-testid={testId}
    >
      {/* Workflow Identifiers */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-blue-400" />
          <span className="text-sm font-medium text-gray-200">Temporal Workflow</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
          <div className="flex items-center justify-between p-2 bg-gray-800 rounded border border-gray-700">
            <span className="text-gray-400">Workflow ID:</span>
            <code className="text-gray-200 font-mono">
              {formatted.workflowId || identifiers.workflowId}
            </code>
          </div>

          {identifiers.runId && (
            <div className="flex items-center justify-between p-2 bg-gray-800 rounded border border-gray-700">
              <span className="text-gray-400">Run ID:</span>
              <code className="text-gray-200 font-mono">
                {formatted.runId}
              </code>
            </div>
          )}

          <div className="flex items-center justify-between p-2 bg-gray-800 rounded border border-gray-700">
            <span className="text-gray-400">Namespace:</span>
            <code className="text-gray-200 font-mono">
              {identifiers.namespace}
            </code>
          </div>
        </div>
      </div>

      {/* Temporal Web Links */}
      <div className="space-y-2">
        <div className="text-xs font-medium text-gray-400 uppercase tracking-wide">
          Temporal Web Links
        </div>

        <div className="flex flex-wrap gap-2">
          {links.workflow && (
            <TemporalLinkButton
              href={links.workflow}
              label="Workflow Execution"
              icon={<Activity className="w-3 h-3" />}
              description="View workflow execution details in Temporal Web"
              variant="primary"
            />
          )}

          {links.history && (
            <TemporalLinkButton
              href={links.history}
              label="Execution History"
              icon={<Clock className="w-3 h-3" />}
              description="View detailed execution history and events"
            />
          )}

          {links.summary && (
            <TemporalLinkButton
              href={links.summary}
              label="Summary"
              icon={<Info className="w-3 h-3" />}
              description="View execution summary and metadata"
            />
          )}

          {links.stackTrace && (
            <TemporalLinkButton
              href={links.stackTrace}
              label="Stack Trace"
              icon={<AlertTriangle className="w-3 h-3" />}
              description="View stack trace for failed workflow"
            />
          )}
        </div>
      </div>

      {/* Debug Information */}
      <div className="text-xs text-gray-500 border-t border-gray-700 pt-2">
        <details>
          <summary className="cursor-pointer hover:text-gray-400">
            Debug Info
          </summary>
          <div className="mt-2 space-y-1 text-xs font-mono bg-gray-900 p-2 rounded">
            <div>Execution ID: {executionId}</div>
            {workflowRunId && <div>Workflow Run ID: {workflowRunId}</div>}
            <div>Full Workflow ID: {identifiers.workflowId}</div>
            {identifiers.runId && <div>Full Run ID: {identifiers.runId}</div>}
            <div className="border-t border-gray-700 pt-2 mt-2">
              <div className="text-gray-400 mb-1">Generated URLs:</div>
              {links.workflow && <div className="break-all">Workflow: {links.workflow}</div>}
              {links.history && <div className="break-all">History: {links.history}</div>}
              {links.summary && <div className="break-all">Summary: {links.summary}</div>}
              {links.stackTrace && <div className="break-all">Stack Trace: {links.stackTrace}</div>}
            </div>
          </div>
        </details>
      </div>
    </div>
  );
}

export default TemporalLinks;